import classNames from 'classnames';
import { CellActionsProvider } from 'context/CellActions';
import { isReadOnlySelector } from 'modules/MedComms/selectors';
import React, { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ArtboardImage from 'components/ArtboardAssets/Image';
import ArtboardCellActions from 'components/ArtboardCellActions';
import ColumnResizer from 'components/ColumnResizer';
import RowResizer from 'components/RowResizer';
import { AccessibilityRole, EntityType, Layer } from 'const';
import Text from 'containers/ArtboardText';
import CallToAction from 'containers/CallToAction';
import { CAROUSEL_ITEM_TYPES } from 'containers/Layouts/sagas/addCarousel';
import { updateAccessibilityRole } from 'containers/Relations/actions';
import { useUpdateCellHeight } from 'hooks/useUpdateCellsHeight';
import * as Models from 'models';
import { eventEmitter, EMITTER_EVENTS } from 'utils/eventEmitter';
import InteractiveCarouselButtonsElement from '../InteractiveCarouselButtonsElement';
import { useArtboardCell } from './hooks';
import styles from './styles.module.scss';
import { useArtboardCellDropTarget } from './useArtboardCellDropTarget';

type Props = {
  cellPosition: number;
  cellsCount: number;
  columnRelationId: string;
  columnsCount: number;
  disableCellWidthEditing: boolean;
  height: number;
  isIncreasingWidthDisabled?: boolean;
  isLayoutEmpty: boolean;
  isReusableLayout: boolean;
  isSectionActive: boolean;
  layout: Models.LayoutMap;
  layoutId: string;
  layoutRelations: Models.LayeredRelationsMap;
  notEditable: boolean;
  parentRelationId: string;
  positionWithinColumn: number;
  relation: Models.LayeredRegularRelationMap<Models.CombinedAssetRelationStyles>;
  sectionStyles: Models.MasterScreen.SectionStylesMap;
  setRowHeightSilently(relationId: string, position: number, height: number): void;
  showColumnResizer?: boolean;
  showRowResizer?: boolean;
  toggleExpandedMode: (isExpandedMode: boolean) => void;
  toggleLayoutRelations(relations: Models.LayeredRelationsMap): void;
  width: number;
  dataTestId: string;
  index: number;
  activeLayer: Layer;
};

const ArtboardCell: React.FunctionComponent<Props> = (props) => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(isReadOnlySelector);

  const {
    activeLayer,
    cellPosition,
    cellsCount,
    disableCellWidthEditing,
    height,
    isIncreasingWidthDisabled = false,
    isLayoutEmpty,
    isSectionActive,
    layoutId,
    layoutRelations,
    notEditable,
    relation,
    sectionStyles,
    showColumnResizer = false,
    showRowResizer = false,
    toggleLayoutRelations,
    width,
    dataTestId,
    index,
    layout,
    isReusableLayout,
  } = props;

  const {
    component,
    container,
    copyingEnabled,
    deletingDisabled,
    document,
    docImageHeight,
    docImageWidth,
    documentImageSrc,
    editMode,
    enterEditMode,
    toggleEditModeOff,
    isAddingDisabled,
    isParentRelationRow,
    isSelected,
    maxCellHeight,
    minCellHeight,
    onAddCell,
    onCopyContent,
    onDeleteCell,
    onImageLoad,
    onSelectHandler,
    onSplitCell,
    toggleColumnWidth,
    toggleRowAndNeighborsHeight,
    toggleRowHeight,
    isAutoFitContent,
    toggleAutoFitContent,
    isResizingRow,
    areActionsDisabled,
    imageCellAccessibilityRole,
    isScreenSwitching,
  } = useArtboardCell(props);

  const changeImageCellAccessibilityRole = useCallback((value: AccessibilityRole): void => {
    if (value === imageCellAccessibilityRole) {
      return;
    }
    dispatch(updateAccessibilityRole(relation.get('id'), value));

  }, [imageCellAccessibilityRole, dispatch, relation]);

  const isLastCell = cellPosition + 1 === cellsCount;
  const showResizer = (isSectionActive || isResizingRow) && showRowResizer && !isReadOnly;

  const carouselItem = layout.get('carouselItem');
  const isCarouselControls = carouselItem?.get('type') === CAROUSEL_ITEM_TYPES.CONTROLS;

  const cellTestId = `${dataTestId}-cell${index}`;

  const { isOver, canDropHere, dropItem, isDraggingAnchor } = useArtboardCellDropTarget({
    container,
    activeLayer,
    relation,
    sectionStyles,
    layoutId,
    notEditable,
    isReadOnly,
    width,
    isCarouselControls,
    isReusableLayout,
  });

  useUpdateCellHeight(relation.get('id'), container.current);

  useEffect(() => {
    if (isReusableLayout) {
      return;
    }
    if (isScreenSwitching && editMode) {
      toggleEditModeOff();
    }
  }, [isScreenSwitching]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleCloseEditMode = (): void => {
      if (editMode) {
        toggleEditModeOff();
      }
    };
    eventEmitter.addListener(EMITTER_EVENTS.CLOSE_CELL_EDIT_MODE, handleCloseEditMode);

    return (): void => {
      eventEmitter.removeListener(EMITTER_EVENTS.CLOSE_CELL_EDIT_MODE, handleCloseEditMode);
    };
  },
  [editMode, toggleEditModeOff]);

  const renderComponent = (): JSX.Element => {
    switch (relation.get('entityType')) {
      case EntityType.TEXT: return (
        <CellActionsProvider
          relationId={relation.get('id')}
          toggleEditModeOn={enterEditMode}
          toggleEditModeOff={toggleEditModeOff}
          containerRef={container}
        >
          <Text
            activeLayer={activeLayer}
            cellHeight={height}
            cellPosition={cellPosition}
            cellWidth={width}
            cellsCount={cellsCount}
            disableCellWidthEditing={disableCellWidthEditing}
            document={document as Models.TextComponentMap}
            dropItem={dropItem} //dropItem only for DraftJS
            editMode={editMode}
            isIncreasingWidthDisabled={isIncreasingWidthDisabled}
            layoutId={layoutId}
            layoutRelations={layoutRelations}
            maxCellHeight={maxCellHeight}
            minCellHeight={minCellHeight}
            notEditable={notEditable}
            relation={relation as Models.LayeredRegularRelationMap<Models.TextRelationStyles>}
            sectionStyles={sectionStyles}
            shrinkable={!isLayoutEmpty}
            toggleColumnWidth={toggleColumnWidth}
            toggleRowAndNeighborsHeight={toggleRowAndNeighborsHeight}
            toggleRowHeight={toggleRowHeight}
            isAutoFitContent={isAutoFitContent}
            toggleAutoFitContent={toggleAutoFitContent}
          />
        </CellActionsProvider>
      );
      case EntityType.LAYOUT_RENDITION:
      case EntityType.IMAGE: return (
        <CellActionsProvider
          relationId={relation.get('id')}
          toggleEditModeOn={enterEditMode}
          toggleEditModeOff={toggleEditModeOff}
          containerRef={container}
        >
          <ArtboardImage
            changeAccessibilityRole={changeImageCellAccessibilityRole}
            currentAccessibilityRole={imageCellAccessibilityRole}
            canDrop={canDropHere}
            cellHeight={height}
            cellPosition={cellPosition}
            cellWidth={width}
            cellsCount={cellsCount}
            disableCellWidthEditing={disableCellWidthEditing}
            documentNumber={document && document.get('number')}
            imageSrc={documentImageSrc}
            docImageWidth={docImageWidth}
            docImageHeight={docImageHeight}
            editMode={editMode}
            isIncreasingWidthDisabled={isIncreasingWidthDisabled}
            layoutId={layoutId}
            layoutRelations={layoutRelations}
            maxCellHeight={maxCellHeight}
            minCellHeight={minCellHeight}
            onImageLoad={onImageLoad}
            relation={relation as Models.LayeredRegularRelationMap<Models.ImageRelationStyles>}
            shrinkable={!isLayoutEmpty}
            toggleColumnWidth={toggleColumnWidth}
            toggleRowAndNeighborsHeight={toggleRowAndNeighborsHeight}
            toggleRowHeight={toggleRowHeight}
            isAutoFitContent={isAutoFitContent}
            toggleAutoFitContent={toggleAutoFitContent}
          />
        </CellActionsProvider>
      );
      case EntityType.CALL_TO_ACTION: return (
        <CellActionsProvider
          relationId={relation.get('id')}
          toggleEditModeOn={enterEditMode}
          toggleEditModeOff={toggleEditModeOff}
          containerRef={container}
        >
          <CallToAction
            cellHeight={height}
            cellWidth={width}
            disableCellWidthEditing={disableCellWidthEditing}
            document={document as Models.CallToActionMap}
            editMode={editMode}
            isIncreasingWidthDisabled={isIncreasingWidthDisabled}
            layoutId={layoutId}
            layoutRelations={layoutRelations}
            maxCellHeight={maxCellHeight}
            minCellHeight={minCellHeight}
            relation={relation as Models.LayeredRegularRelationMap<Models.CallToActionStyles>}
            toggleColumnWidth={toggleColumnWidth}
            toggleRowAndNeighborsHeight={toggleRowAndNeighborsHeight}
            toggleRowHeight={toggleRowHeight}
            isAutoFitContent={isAutoFitContent}
            toggleAutoFitContent={toggleAutoFitContent}
          />
        </CellActionsProvider>
      );
      case EntityType.INTERACTIVE_CAROUSEL_BUTTONS_ELEMENT: return (
        <InteractiveCarouselButtonsElement />
      );
      default: return null;
    }
  };

  return (
    <div
      ref={container}
      onDoubleClick={enterEditMode}
      className={classNames(styles.ArtboardCell, {
        [styles.dropHotspot]: canDropHere && !isDraggingAnchor,
        [styles.stretchable]: isLastCell && !height,
      })}
      style={{
        maxWidth: width || 'none',
        // do not override flex-basis of last cell, it's defined in css
        ...width && isParentRelationRow
          ? { flexBasis: width }
          : !isLastCell
            ? { flexBasis: 'auto' }
            : {},
        ...height ? { height } : {},
      }}
      data-testid={cellTestId}
    >
      {
        showColumnResizer &&
        <ColumnResizer
          container={container}
          layoutId={layoutId}
          relationIsBeingResizedId={relation.get('id')}
          relations={layoutRelations}
          toggleRelations={toggleLayoutRelations}
        />
      }
      {
        // initially sliders are shown only within active section,
        // but when user resizes row on border of two section and move away from active one we should not hide slider
        showResizer &&
        <RowResizer
          container={container}
          height={height}
          layoutId={layoutId}
          relationIsBeingResizedId={relation.get('id')}
          relations={layoutRelations}
          toggleRelations={toggleLayoutRelations}
        />
      }
      {
        (notEditable || isCarouselControls)
          ?
          <div className={styles.ArtboardCellWrapper}>{renderComponent()}</div>
          :
          <ArtboardCellActions
            addCell={onAddCell}
            areActionsDisabled={areActionsDisabled}
            canDrop={canDropHere}
            cellWidth={width}
            copyContent={onCopyContent}
            copyingEnabled={copyingEnabled}
            deleteCell={onDeleteCell}
            deletingDisabled={deletingDisabled || isReadOnly}
            isAddingDisabled={isAddingDisabled || isReadOnly}
            isOver={isOver}
            isEditMode={editMode}
            isSelected={isSelected}
            outlineOnly={editMode}
            splitCell={onSplitCell}
            component={component}
            document={document}
            onSelectHandler={onSelectHandler}
            dataTestId={cellTestId}
          >
            {renderComponent()}
          </ArtboardCellActions>
      }
    </div>
  );
};

export default ArtboardCell;
